import CssBaseline from "@mui/material/CssBaseline";
import { ThemeProvider } from "@mui/material/styles";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { useEffect, type ReactNode } from "react";
import { RouterProvider } from "react-router";
import { setTelemetryCallback } from "./api";
import { ErrorBoundary, RttDisplay } from "./components";
import {
  HistoryProvider,
  NotificationProvider,
  useFeatures,
  useOnlineStatus,
  useRoundTripTime,
} from "./hooks";
import { router } from "./routes";
import { theme } from "./theme";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60, // 1 minute
      retry: 2,
    },
  },
});

function AppContent() {
  const { latestRtt, lastMeasuredAt, updateRtt } = useRoundTripTime();
  // Activate online/offline detection toasts
  useOnlineStatus();

  // Set up telemetry callback on mount
  useEffect(() => {
    setTelemetryCallback(updateRtt);
    return () => {
      setTelemetryCallback(null);
    };
  }, [updateRtt]);

  return (
    <>
      <RouterProvider router={router} />
      <RttDisplay rtt={latestRtt} measuredAt={lastMeasuredAt} />
    </>
  );
}

function FeatureAwareHistoryProvider({ children }: { children: ReactNode }) {
  const { data: features } = useFeatures();
  const persistenceEnabled = features?.persistentValidationHistory ?? false;

  return (
    <HistoryProvider persistenceEnabled={persistenceEnabled}>
      {children}
    </HistoryProvider>
  );
}

export function App() {
  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <ThemeProvider theme={theme}>
          <CssBaseline />
          <NotificationProvider>
            <FeatureAwareHistoryProvider>
              <AppContent />
            </FeatureAwareHistoryProvider>
          </NotificationProvider>
        </ThemeProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  );
}
